import { NeedDto, SurveyV2 } from '@act/shared/data-transfer-objects';
import { Goal } from '../types';
import { DateTime } from 'luxon';

export function sortAggregatedGoals(
  goals: Goal[],
  direction: 'ASC' | 'DESC' = 'ASC'
) {
  const aggregatedGoalsMap = [...goals].reduce(
    (agg, goal) => {
      if (goal.goalType === 'consent' && !goal.completed) {
        return {
          ...agg,
          nonCompleteConsents: [...agg.nonCompleteConsents, goal],
        };
      }
      if (
        goal.goalType === 'survey' &&
        !(goal as SurveyV2.AssignedSurveyDto).dueAt
      ) {
        return {
          ...agg,
          surveysWithoutDueDates: [...agg.surveysWithoutDueDates, goal],
        };
      }
      return { ...agg, unsortedGoals: [...agg.unsortedGoals, goal] };
    },
    {
      nonCompleteConsents: [],
      unsortedGoals: [],
      surveysWithoutDueDates: [],
    }
  );

  const sortedGoals = [...aggregatedGoalsMap.unsortedGoals].sort((a, b) => {
    const aDueDate = dueDateParser(a);
    const bDueDate = dueDateParser(b);

    return direction === 'ASC' ? aDueDate - bDueDate : bDueDate - aDueDate;
  });

  return [
    ...aggregatedGoalsMap.nonCompleteConsents,
    ...sortedGoals,
    ...aggregatedGoalsMap.surveysWithoutDueDates,
  ];
}

function dueDateParser(goal: Goal): number {
  switch (goal.goalType) {
    case 'consent':
      if (goal.completed) {
        return goal.completedAt;
      }
      return -5;
    case 'need':
      return (goal as NeedDto)?.dueDate;
    case 'survey':
      return surveyDueDateParser(goal as SurveyV2.AssignedSurveyDto);
    default:
      return -1;
  }
}

function surveyDueDateParser(
  assignedSurvey: SurveyV2.AssignedSurveyDto
): number {
  const currentPhase = assignedSurvey.phases
    .filter(phase => phase.checkpoint && phase.completedAt)
    .sort((a, b) => b.completedAt - a.completedAt)[0];

  const dueAt =
    assignedSurvey.completedAt ||
    currentPhase?.nextPhaseDueAt ||
    assignedSurvey.dueAt ||
    0;

  return DateTime.fromJSDate(new Date(dueAt)).toMillis();
}
